import type { ColumnConfig, ColumnGroup } from './types';

/** Fixed width of the leading action/expand column. Shared with
 *  EditableTable's colgroup and cell styles. */
export const ACTION_COL_WIDTH = 40;

export interface StickyMeta {
  /** Whether any data column is sticky */
  hasStickyColumns: boolean;
  /** Map of col.data → left offset in pixels */
  offsets: Map<string, number>;
  /** The `data` field of the last contiguous sticky column (for shadow divider) */
  lastStickyColData: string | null;
  /** Whether the action column should be sticky (auto-true when any data column is sticky) */
  actionColSticky: boolean;
}

const EMPTY_META: StickyMeta = {
  hasStickyColumns: false,
  offsets: new Map(),
  lastStickyColData: null,
  actionColSticky: false,
};

interface ComputeStickyMetaOptions {
  hideActionColumn?: boolean;
  readOnly?: boolean;
  columnGroups?: ColumnGroup[];
  hasExpandColumn?: boolean;
}

/**
 * Compute sticky column metadata from column config.
 *
 * Sticky columns must be contiguous from the left and have explicit `width`.
 * The action column (when present) auto-sticks when any data column is sticky.
 * When `hasExpandColumn` is true, the action column is shown (for the expand
 * chevron) even if `hideActionColumn` is set — and it sticks accordingly.
 * Mixed sticky/non-sticky column groups are auto-promoted with a warning.
 */
export function computeStickyMeta(
  columns: ColumnConfig[],
  options: ComputeStickyMetaOptions = {},
): StickyMeta {
  const { hideActionColumn = false, readOnly = false, columnGroups, hasExpandColumn = false } = options;

  // The action/expand column is always visible when either action buttons or expand chevron are present
  const hasFirstCol = (!readOnly && !hideActionColumn) || hasExpandColumn;

  if (!columns.length) {
    return hasFirstCol
      ? { hasStickyColumns: false, offsets: new Map(), lastStickyColData: null, actionColSticky: true }
      : EMPTY_META;
  }

  // Auto-promote mixed groups: if any column in a group is sticky, all must be
  const promotedStickySet = new Set<string>();
  if (columnGroups?.length) {
    for (const group of columnGroups) {
      const groupCols = columns.filter(c => c.group === group.key);
      if (groupCols.length === 0) continue;
      const stickyCount = groupCols.filter(c => c.sticky).length;
      if (stickyCount > 0 && stickyCount < groupCols.length) {
        console.warn(
          `EditableTable: Column group "${group.key}" has mixed sticky/non-sticky columns. ` +
          `All columns in the group will be promoted to sticky.`
        );
        for (const c of groupCols) {
          promotedStickySet.add(c.data);
        }
      }
    }
  }

  const isSticky = (col: ColumnConfig) => col.sticky || promotedStickySet.has(col.data);

  // Collect contiguous sticky columns from the left
  const stickyColumns: ColumnConfig[] = [];
  for (const col of columns) {
    if (!isSticky(col)) break;
    if (!col.width) {
      console.warn(
        `EditableTable: Sticky column "${col.data}" is missing an explicit \`width\`. ` +
        `Sticky offset calculation requires known widths. Defaulting to 120px.`
      );
    }
    stickyColumns.push(col);
  }

  if (stickyColumns.length === 0) {
    return hasFirstCol
      ? { hasStickyColumns: false, offsets: new Map(), lastStickyColData: null, actionColSticky: true }
      : EMPTY_META;
  }

  // Warn about non-contiguous sticky columns that won't be pinned
  const skippedSticky = columns.slice(stickyColumns.length).filter(isSticky);
  if (skippedSticky.length > 0) {
    console.warn(
      `EditableTable: Columns [${skippedSticky.map(c => c.data).join(', ')}] have \`sticky: true\` ` +
      `but are not contiguous from the left. They will not be pinned.`
    );
  }

  let currentLeft = hasFirstCol ? ACTION_COL_WIDTH : 0;
  const offsets = new Map<string, number>();

  for (const col of stickyColumns) {
    offsets.set(col.data, currentLeft);
    currentLeft += col.width || 120;
  }

  return {
    hasStickyColumns: true,
    offsets,
    lastStickyColData: stickyColumns[stickyColumns.length - 1].data,
    actionColSticky: hasFirstCol,
  };
}

/**
 * Nearest *actually-scrolling* ancestor of `el` — the first ancestor whose
 * computed `overflow-y` is auto/scroll AND that currently clips its content
 * (`scrollHeight > clientHeight`) — the app content scroller the floating header
 * / sticky toolbar snap to. Returns null → snap to the viewport top.
 *
 * The clipping check is what lets us skip a *declared-but-non-scrolling*
 * container. shadcn's `TabsContent` carries `data-[state=active]:overflow-y-auto`
 * yet is auto-height and grows with its content (its companion `flex-1` is inert
 * unless the `Tabs` root is a bounded flex column), so it never clips and must
 * NOT be mistaken for the scroll viewport. Without this guard a grid hosted
 * inside a Tab would snap to the TabsContent — which scrolls *with* the page, so
 * the header can never cross above it — and the floating header / sticky toolbar
 * would silently never trigger (Expenses, Funds). With it, the walk falls
 * through to the real app scroller (`AppLayout`'s `flex-1 overflow-y-auto`),
 * whose top is fixed.
 *
 * Because a real scroller only clips once its content overflows, this can return
 * null before the grid is tall enough — callers re-resolve lazily until they
 * latch one. Shared by `FloatingTableHeader` and the sticky-toolbar controller
 * in `EditableTable`.
 */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Whether the floating header should show, given viewport-space measurements.
 * Show once the real header has scrolled above the snap line (`headerTop` past
 * `snapTop`, with a sub-pixel epsilon) AND the grid body still extends far
 * enough below the line to sit under the overlay (`scrollBottom` clears the
 * overlay's own height). Pure so the gate is unit-testable without layout.
 */
export function shouldShowFloatingHeader(
  headerTop: number,
  scrollBottom: number,
  snapTop: number,
  headerHeight: number,
): boolean {
  return headerTop < snapTop - 0.5 && scrollBottom > snapTop + headerHeight;
}

/**
 * The nearest ancestor that becomes the containing block of a
 * `position: fixed` descendant (a transform — incl. the individual
 * translate / scale / rotate properties — perspective, a filter or
 * backdrop-filter, `will-change` of one of those, or `contain: paint` /
 * `layout`), or null when it is the viewport.
 */
export function fixedContainingBlock(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const cs = getComputedStyle(node);
    if (
      (cs.transform && cs.transform !== 'none') ||
      // Tailwind v4 writes translate-* / scale-* / rotate-* to the individual properties.
      (cs.translate && cs.translate !== 'none') ||
      (cs.scale && cs.scale !== 'none') ||
      (cs.rotate && cs.rotate !== 'none') ||
      (cs.perspective && cs.perspective !== 'none') ||
      (cs.backdropFilter && cs.backdropFilter !== 'none') ||
      /transform|translate|scale|rotate|perspective|filter|contain/.test(cs.willChange || '') ||
      (cs.filter && cs.filter !== 'none') ||
      /paint|layout|strict|content/.test(cs.contain || '')
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * The ancestors of `el`, up to and including `block`, that clip their
 * content vertically (overflow-y auto / scroll / hidden / clip) — the table's
 * frame, a dialog's scrolling body — innermost first.
 */
export function clippingAncestorsWithin(el: HTMLElement, block: HTMLElement): HTMLElement[] {
  const clips: HTMLElement[] = [];
  let node = el.parentElement;
  while (node) {
    if (/auto|scroll|hidden|clip/.test(getComputedStyle(node).overflowY)) clips.push(node);
    if (node === block) break;
    node = node.parentElement;
  }
  return clips;
}
